/**
 * Touch input handler. Mirrors `MouseHandler`'s callback contract so the same
 * `applyMouseDelta` / `applyZoom` consumers can be driven by mobile gestures:
 *   - one finger drag  → `dragCb(dx, dy)` (1 px → 1 unit, matches mouse)
 *   - two finger pinch → `scrollCb(prevDist - newDist)` (positive on
 *     shrinking distance, matching `WheelEvent.deltaY` polarity for zoom-out)
 *   - three or more fingers → ignored until the user lifts back to ≤ 2
 *
 * All listeners use `{ passive: false }` so `preventDefault()` actually
 * suppresses the browser's default scroll/zoom on iPad/Android.
 */
type Mode = 'idle' | 'dragging' | 'pinching';

interface TouchPoint {
    clientX: number;
    clientY: number;
}

export class TouchHandler {
    private el: HTMLElement;
    private dragCb: ((dx: number, dy: number) => void) | null = null;
    private scrollCb: ((d: number) => void) | null = null;
    private mode: Mode = 'idle';
    private lastX = 0;
    private lastY = 0;
    private prevPinchDist = 0;
    private handlers: { [k: string]: (e: any) => void } = {};

    constructor(domElement: HTMLElement) {
        this.el = domElement;
        this.handlers = {
            touchstart: (e: TouchEvent) => this.onTouchStart(e),
            touchmove: (e: TouchEvent) => this.onTouchMove(e),
            touchend: (e: TouchEvent) => this.onTouchEnd(e),
            touchcancel: (e: TouchEvent) => this.onTouchEnd(e),
        };
    }

    onDrag(cb: (dx: number, dy: number) => void): void { this.dragCb = cb; }
    onScroll(cb: (d: number) => void): void { this.scrollCb = cb; }

    attach(): void {
        for (const [k, v] of Object.entries(this.handlers)) {
            this.el.addEventListener(k, v, { passive: false });
        }
    }

    detach(): void {
        for (const [k, v] of Object.entries(this.handlers)) {
            this.el.removeEventListener(k, v);
        }
        this.mode = 'idle';
    }

    private onTouchStart(e: TouchEvent): void {
        e.preventDefault();
        const touches = e.touches;
        const n = touches?.length ?? 0;
        if (n === 1) {
            this.beginDrag(touches[0]);
        } else if (n === 2) {
            this.beginPinch(touches[0], touches[1]);
        } else {
            // 3+ fingers: ignore until count drops back to ≤ 2.
            this.mode = 'idle';
        }
    }

    private onTouchMove(e: TouchEvent): void {
        e.preventDefault();
        const touches = e.touches;
        const n = touches?.length ?? 0;
        if (this.mode === 'dragging' && n === 1) {
            const t = touches[0];
            const dx = t.clientX - this.lastX;
            const dy = t.clientY - this.lastY;
            if (dx !== 0 || dy !== 0) this.dragCb?.(dx, dy);
            this.lastX = t.clientX;
            this.lastY = t.clientY;
        } else if (this.mode === 'pinching' && n === 2) {
            const dist = pinchDistance(touches[0], touches[1]);
            const delta = this.prevPinchDist - dist;
            if (delta !== 0) this.scrollCb?.(delta);
            this.prevPinchDist = dist;
        }
        // 3+ touches or mismatched mode: no-op.
    }

    private onTouchEnd(e: TouchEvent): void {
        e.preventDefault();
        const remaining = e.touches?.length ?? 0;
        if (remaining === 1) {
            // 2→1 transition: recapture the surviving finger so the next
            // move doesn't bake in the gap between the two pinch fingers
            // as a giant drag delta.
            this.beginDrag(e.touches[0]);
        } else if (remaining === 0) {
            this.mode = 'idle';
        } else if (remaining >= 2) {
            // Unusual: e.g. 4→2. Re-baseline pinch from the remaining pair.
            this.beginPinch(e.touches[0], e.touches[1]);
        }
    }

    private beginDrag(t: TouchPoint): void {
        this.mode = 'dragging';
        this.lastX = t.clientX;
        this.lastY = t.clientY;
    }

    private beginPinch(a: TouchPoint, b: TouchPoint): void {
        this.mode = 'pinching';
        this.prevPinchDist = pinchDistance(a, b);
    }
}

function pinchDistance(a: TouchPoint, b: TouchPoint): number {
    return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}
